import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { MapContainer, TileLayer, GeoJSON, LayersControl, useMap, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { FaCircleInfo } from "react-icons/fa6";

import { Box, Typography, IconButton } from '@mui/material';

import parseGeoraster from "georaster";
import GeoRasterLayer from "georaster-layer-for-leaflet";
import centroid from "@turf/centroid";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { motion, AnimatePresence } from "framer-motion";
import CommonTable from "../../../../common/CommonTable";
import { MapHomeControl, MapLegendControl } from "../../../../common/MapControls/MapControls";

const INITIAL_VIEW_STATE = {
  longitude: 125.7275,
  latitude: -8.8742,
  zoom: 8,
};

const RISK_CATEGORIES = [
  { label: "No-Risk", key: "No-Risk", color: "rgb(148, 203, 85)" },
  { label: "Low-Risk", key: "Low-Risk", color: "rgb(243, 235, 18)" },
  { label: "Medium-Risk", key: "Medium-Risk", color: "rgb(243, 155, 58)" },
  { label: "High-Risk", key: "High-Risk", color: "rgb(230, 45, 58)" },
];

const INFO_RISK_CATEGORIES = [
  ...RISK_CATEGORIES,
  { label: "No Data", key: "No Data", color: "rgb(222,222,223)" }
];

const InfoButton = ({ onClick }) => {
  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const btnRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  return (
    <div
      ref={btnRef}
      style={{
        position: "absolute",
        bottom: "20px",
        left: "12px",
        padding: "8px",
        borderRadius: "8px",
        fontSize: "15px",
        cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
        zIndex: 1000,
      }}
      onClick={onClick}
    >
      <FaCircleInfo />
    </div>
  );
};

const Legend = () => (
  <MapLegendControl sections={[{ items: INFO_RISK_CATEGORIES }]} />
);

const GeoTiffLayerComponent = ({ georaster }) => {
  const map = useMap();

  useEffect(() => {
    if (!georaster || !map) return;
    const colorFn = values => {
      const pixelValue = values[0];
      if (pixelValue === 4) return 'rgb(230, 45, 58)';
      if (pixelValue === 3) return 'rgb(243, 155, 58)';
      if (pixelValue === 2) return 'rgb(243, 235, 18)';
      if (pixelValue === 1) return 'rgb(148, 203, 85)';
      return null;
    };
    const layer = new GeoRasterLayer({
      georaster: georaster,
      opacity: 0.7,
      pixelValuesToColorFn: colorFn,
      resolution: 256,
    });
    layer.addTo(map);

    // animate: false avoids a Leaflet bug where a stale zoom-transition
    // callback fires after the layer/map has already been torn down
    // (e.g. rapid season switching), crashing on a removed map pane.
    map.fitBounds(layer.getBounds(), { animate: false });

    return () => {
      // map.stop() itself touches _mapPane internally, which react-leaflet's
      // MapContainer may have already deleted via map.remove() if this
      // cleanup runs as part of the whole map unmounting — guard for that.
      if (map._mapPane) {
        map.stop();
      }
      if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    };
  }, [map, georaster]);

  return null;
};

const getCurrentTimorLesteSeason = () => {
  const now = new Date();
  const currentMonth = now.getUTCMonth() + 1;
  const currentDay = now.getUTCDate();
  if ((currentMonth === 12 && currentDay >= 1) || (currentMonth >= 1 && currentMonth <= 5)) {
    return "rainy";
  } else {
    return "dry";
  }
};

const SeasonalWise = () => {
  const [boundaryData, setBoundaryData] = useState(null);
  const [info, setInfo] = useState(false);
  const infoRef = useRef(null);
  const infoPanelRef = useCallback((node) => {
    infoRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  useEffect(() => {
    if (!info) return;
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setInfo(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [info]);
  const [georaster, setGeoraster] = useState(null);
  const [riskDetails, setRiskDetails] = useState([]);
  const [isLoadingTiff, setIsLoadingTiff] = useState(false);
  const [clickedSucoPopup, setClickedSucoPopup] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(getCurrentTimorLesteSeason());
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const filterPanelRef = useRef(null);

  const tiffUrls = useMemo(() => ({
    rainy: "/DATA/Landslide/Susceptibility/SeasonalWise/TimorLeste_LSM_Dynamic_2023-12-01_to_2024-05-31.tif",
    dry: "/DATA/Landslide/Susceptibility/SeasonalWise/TimorLeste_LSM_Dynamic_2024-06-01_to_2024-11-30.tif"
  }), []);

  const tiffUrl = useMemo(() => tiffUrls[selectedSeason], [selectedSeason, tiffUrls]);

  const tableData = useMemo(
    () => riskDetails.map((item) => ({ ...item, advisory: item.riskLevel })),
    [riskDetails],
  );

  const legendColors = useMemo(
    () =>
      INFO_RISK_CATEGORIES.reduce(
        (acc, cat) => ({ ...acc, [cat.key]: cat.color }),
        { All: '#f0f0f0' },
      ),
    [],
  );

  const columns = useMemo(
    () => [
      { key: 'SlNo', label: 'S.No.' },
      { key: 'ADM1', label: 'Municipality', sortable: true },
      { key: 'ADM2', label: 'Post Admin', sortable: true },
      { key: 'ADM3', label: 'Suco', sortable: true },
      {
        key: 'riskLevel',
        label: 'Risk Level',
        sortable: true,
        render: (row) => {
          const category = INFO_RISK_CATEGORIES.find(cat => cat.key === row.riskLevel);
          const backgroundColor = category ? category.color : 'grey';
          return (
            <span
              style={{
                backgroundColor: backgroundColor,
                color: 'black',
                fontWeight: 'bold',
                padding: '2px 5px',
                borderRadius: '3px',
                display: 'inline-block'
              }}
            >
              {row.riskLevel}
            </span>
          );
        },
      },
      {
        key: 'Population',
        label: 'Total Population',
        sortable: true,
        render: (row) => row.Population?.toLocaleString() || 'N/A',
      },
      {
        key: 'Male',
        label: 'Total Male',
        sortable: true,
        render: (row) => row.Male?.toLocaleString() || 'N/A',
      },
      {
        key: 'Female',
        label: 'Total Female',
        sortable: true,
        render: (row) => row.Female?.toLocaleString() || 'N/A',
      },
      {
        key: 'HH_Urban',
        label: 'HH Urban',
        sortable: true,
        render: (row) => row.HH_Urban?.toLocaleString() || 'N/A',
      },
      {
        key: 'HH_Rural',
        label: 'HH Rural',
        sortable: true,
        render: (row) => row.HH_Rural?.toLocaleString() || 'N/A',
      },
      {
        key: 'Household',
        label: 'Households',
        sortable: true,
        render: (row) => row.Household?.toLocaleString() || 'N/A',
      },
    ],
    [],
  );

  useEffect(() => {
    const fetchBoundaryData = async () => {
      try {
        const response = await fetch("/DATA/OSMbased_adm3_boundary.json");
        const json = await response.json();
        setBoundaryData(json);
        console.log("Boundary data loaded.");
      } catch (error) {
        console.error("Error loading boundary data:", error);
      }
    };
    fetchBoundaryData();
  }, []);

  useEffect(() => {
    const fetchTiffData = async () => {
      if (!tiffUrl) return;

      setIsLoadingTiff(true);
      setGeoraster(null);
      setRiskDetails([]);
      setClickedSucoPopup(null);

      try {
        console.log(`Fetching GeoTIFF for ${selectedSeason} season from:`, tiffUrl);
        const response = await fetch(tiffUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch GeoTIFF: ${response.status} ${response.statusText}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        const parsedGeoraster = await parseGeoraster(arrayBuffer);
        setGeoraster(parsedGeoraster);
        console.log("GeoTIFF loaded and parsed.");
      } catch (error) {
        console.error("Error loading or parsing GeoTIFF:", error);
        setGeoraster(null);
      } finally {
        setIsLoadingTiff(false);
      }
    };

    fetchTiffData();
  }, [tiffUrl, selectedSeason]);

  useEffect(() => {
    if (!georaster || !boundaryData) {
        setRiskDetails([]);
        console.log("Waiting for georaster and boundaryData to load for analysis.");
        return;
    }

    console.log("Starting risk analysis: Using Centroid Sampling for Suco Risk Level.");
    let identifiedSucoDetails = [];
    const { xmin, ymax, pixelWidth, pixelHeight, values } = georaster;

    if (!values || values.length === 0) {
        console.error("GeoTIFF values are missing or empty.");
        setRiskDetails([]);
        return;
    }
    const rasterValues = values[0];

    boundaryData.features.forEach(feature => {
      if (feature.geometry && feature.properties) {
        const featureCentroid = centroid(feature.geometry);
        const [lng, lat] = featureCentroid.geometry.coordinates;

        if (pixelWidth === 0 || pixelHeight === 0 || georaster.width === 0 || georaster.height === 0) {
            const sucoData = {
                ...feature.properties,
                riskLevel: 'No Data',
                riskColor: INFO_RISK_CATEGORIES.find(c => c.key === 'No Data').color
            };
            identifiedSucoDetails.push(sucoData);
            return;
        }

        const x = Math.floor((lng - xmin) / pixelWidth);
        const y = Math.floor((ymax - lat) / pixelHeight);

        let riskLevel;
        let riskColor;

        if (x >= 0 && x < georaster.width && y >= 0 && y < georaster.height) {
          if (rasterValues && rasterValues[y] !== undefined && rasterValues[y][x] !== undefined) {
            const pixelValue = rasterValues[y][x];

            switch (pixelValue) {
              case 4:
                riskLevel = 'High-Risk';
                riskColor = INFO_RISK_CATEGORIES.find(c => c.key === 'High-Risk').color;
                break;
              case 3:
                riskLevel = 'Medium-Risk';
                riskColor = INFO_RISK_CATEGORIES.find(c => c.key === 'Medium-Risk').color;
                break;
              case 2:
                riskLevel = 'Low-Risk';
                riskColor = INFO_RISK_CATEGORIES.find(c => c.key === 'Low-Risk').color;
                break;
              case 1:
                riskLevel = 'No-Risk';
                riskColor = INFO_RISK_CATEGORIES.find(c => c.key === 'No-Risk').color;
                break;
              default:
                riskLevel = 'No Data';
                riskColor = INFO_RISK_CATEGORIES.find(c => c.key === 'No Data').color;
                console.warn(`Unexpected pixel value for ${feature.properties.ADM3}: ${pixelValue}. Assigning to 'No Data'.`);
                break;
            }
          } else {
            riskLevel = 'No Data';
            riskColor = INFO_RISK_CATEGORIES.find(c => c.key === 'No Data').color;
          }
        } else {
            riskLevel = 'No Data';
            riskColor = INFO_RISK_CATEGORIES.find(c => c.key === 'No Data').color;
        }

        const sucoData = {
          ...feature.properties,
          riskLevel: riskLevel,
          riskColor: riskColor
        };
        identifiedSucoDetails.push(sucoData);
      }
    });

    identifiedSucoDetails.sort((a, b) => {
        if (a.ADM1 < b.ADM1) return -1;
        if (a.ADM1 > b.ADM1) return 1;
        if (a.ADM2 < b.ADM2) return -1;
        if (a.ADM2 > b.ADM2) return 1;
        return a.ADM3.localeCompare(b.ADM3);
    });

    identifiedSucoDetails = identifiedSucoDetails.map((item, index) => ({
      ...item,
      sNo: index + 1
    }));

    setRiskDetails(identifiedSucoDetails);
    console.log(`Risk analysis complete. Processed ${identifiedSucoDetails.length} sucos.`);
  }, [georaster, boundaryData]);


  const handleSucoClick = useCallback((feature, latlng) => {
    const properties = feature.properties;

    const sucoDetailFromAnalysis = riskDetails.find(
      (detail) =>
        detail.ADM1 === properties.ADM1 &&
        detail.ADM2 === properties.ADM2 &&
        detail.ADM3 === properties.ADM3
    );

    const displayDetails = sucoDetailFromAnalysis || {
        ...properties,
        riskLevel: 'No Data',
        riskColor: INFO_RISK_CATEGORIES.find(c => c.key === 'No Data').color,
    };

    setClickedSucoPopup({
      position: latlng,
      details: displayDetails,
    });
  }, [riskDetails]);

  const onEachSucoFeature = useCallback((feature, layer) => {
    layer.on({
      click: (e) => {
        handleSucoClick(feature, e.latlng);
      },
    });
  }, [handleSucoClick]);

  const handleSeasonChange = (newSeason) => {
    setSelectedSeason(newSeason);
  };

  const sucoStyle = useCallback((feature) => {
    if (!feature || !feature.properties || riskDetails.length === 0) {
      return {
        color: "#000000",
        weight: 1,
        opacity: 0.8,
        fillOpacity: 0.05,
        fillColor: 'lightgrey'
      };
    }

    const sucoAnalysis = riskDetails.find(
      (detail) =>
        detail.ADM1 === feature.properties.ADM1 &&
        detail.ADM2 === feature.properties.ADM2 &&
        detail.ADM3 === feature.properties.ADM3
    );

    const effectiveRiskLevel = sucoAnalysis?.riskLevel || 'No Data';
    const category = INFO_RISK_CATEGORIES.find(cat => cat.key === effectiveRiskLevel);
    const color = category ? category.color : 'lightgrey';
    const fillOpacity = effectiveRiskLevel === 'No Data' ? 0.05 : 0.4;

    return {
      color: "#000000",
      weight: 1,
      opacity: 0.8,
      fillColor: color,
      fillOpacity: fillOpacity,
    };
  }, [riskDetails]);

  return (
    <div className="wind-speed-cyclone-container">
      <div className="map-container-WindSpeedCyclone">
        <MapContainer
          center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
          zoom={INITIAL_VIEW_STATE.zoom}
          className="mapcontainerall"
        >
          <MapHomeControl />
            <Box
              sx={{ position: "absolute", top: 10, left: 50, zIndex: 1000 }}
              ref={filterPanelRef}
            >
              <IconButton
                color="primary"
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                data-filter-toggle
              >
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "50%",
                    padding: "8px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: 1,
                  }}
                >
                  <FilterAltIcon sx={{ color: "#0d6efd", fontSize: 32 }} />
                </Box>
              </IconButton>
  
              <AnimatePresence>
                {showFilterPanel && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                  <div className="FilterPanel" style={{ width: 'auto' }}>
                  <div className="FilterPanel-container">
                      <h4 className="dropdown-heading">Select Season:</h4>
                      <select id="season-select" className="dropdown" value={selectedSeason} onChange={(e) => handleSeasonChange(e.target.value)}>
                        <option value="rainy">Rainy (Dec 1 - May 31)</option>
                        <option value="dry">Dry (Jun 1 - Nov 30)</option>
                      </select>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>

          {info && (
            <div
              ref={infoPanelRef}
              style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
            >
              Seasonal wise model using Random Forest Model and properties used slope, aspect, curvature, ndvi, rainfall(chirps), distRoad, distStream, lulc.
              </div>
          )}
          <InfoButton onClick={() => setInfo(!info)} />

          <LayersControl position="topright">
            <LayersControl.BaseLayer name="White Theme" checked>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="OpenStreetMap">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>

            <LayersControl.Overlay name={`Landslide Susceptibility (${selectedSeason})`} checked>
              {isLoadingTiff && <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1001, background: 'rgba(255,255,255,0.8)', padding: '10px', borderRadius: '5px'}}>Loading Map Data...</div>}
              {georaster && !isLoadingTiff && <GeoTiffLayerComponent georaster={georaster} />}
            </LayersControl.Overlay>

            {boundaryData && (
              <LayersControl.Overlay name="Suco Boundaries" checked>
                <GeoJSON
                  key={selectedSeason + (riskDetails.length > 0 ? "-loaded" : "-loading")}
                  data={boundaryData}
                  style={sucoStyle}
                  onEachFeature={onEachSucoFeature}
                />
              </LayersControl.Overlay>
            )}
          </LayersControl>

          {clickedSucoPopup && clickedSucoPopup.details && (
            <Popup
              position={clickedSucoPopup.position}
              onClose={() => setClickedSucoPopup(null)}
            >
              <div style={{overflowY: 'auto', paddingRight: '10px'}}>
                <h5>Suco Details:</h5>
                <p><strong>Municipality:</strong> {clickedSucoPopup.details.ADM1 || 'N/A'}</p>
                <p><strong>Post Admin:</strong> {clickedSucoPopup.details.ADM2 || 'N/A'}</p>
                <p><strong>Suco:</strong> {clickedSucoPopup.details.ADM3 || 'N/A'}</p>
                <hr style={{margin: '5px 0'}}/>
                <p><strong>Total Population:</strong> {clickedSucoPopup.details.Population?.toLocaleString() || 'N/A'}</p>
                <p><strong>Total Male:</strong> {clickedSucoPopup.details.Male?.toLocaleString() || 'N/A'}</p>
                <p><strong>Total Female:</strong> {clickedSucoPopup.details.Female?.toLocaleString() || 'N/A'}</p>
                <p><strong>Households:</strong> {clickedSucoPopup.details.Household?.toLocaleString() || 'N/A'}</p>
                {clickedSucoPopup.details.riskLevel && (
                  <p><strong>Risk Level:</strong>
                    <span style={{
                      backgroundColor: clickedSucoPopup.details.riskColor,
                      color: 'black',
                      fontWeight: 'bold',
                      padding: '2px 5px',
                      marginLeft: '5px',
                      borderRadius: '3px',
                      display: 'inline-block'
                    }}>
                      {clickedSucoPopup.details.riskLevel}
                    </span>
                  </p>
                )}
              </div>
            </Popup>
          )}

          <Legend />
        </MapContainer>

        <Box sx={{ p: 2, borderTop: '1px solid #ddd', overflow: 'auto' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                Suco-level Risk Details
            </Typography>
            {isLoadingTiff ? (
                <Typography>Loading seasonal risk data for table...</Typography>
            ) : (
                <CommonTable
                    key={selectedSeason}
                    tableData={tableData}
                    columns={columns}
                    showFilter
                    filterOptions={['All', ...INFO_RISK_CATEGORIES.map(cat => cat.key)]}
                    legendColors={legendColors}
                    searchableKeys={['ADM1', 'ADM2', 'ADM3']}
                    tableName="Landslide_SeasonalWise"
                    searchPlaceholder="Search by Municipality, Post Admin, Suco..."
                    emptyMessage="No sucos found matching the current filters for the selected season."
                />
            )}
        </Box>
      </div>
    </div>
  );
};

export default SeasonalWise;